import React, { useState, useEffect } from 'react';
import { useAuth, apiFetch } from '../App';
import NINVerification from '../components/NINVerification';
import { useNavigate } from 'react-router-dom';

const TABS = [
  { id:'stakes',    icon:'🎯', label:'My Bets'   },
  { id:'top',       icon:'🏆', label:'Top'        },
  { id:'invite',    icon:'👥', label:'Invite'     },
  { id:'help',      icon:'❓', label:'Help'       },
  { id:'account',   icon:'👤', label:'Account'    },
];

export default function ProfilePage() {
  const { user, logout, showToast, loadMe } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]           = useState('stakes');
  const [stakes, setStakes]     = useState([]);
  const [refData, setRefData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');

  const [linking, setLinking]       = useState(false);
  const [disputeModal, setDisputeModal] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [supportMsg, setSupportMsg] = useState('');
  const [supportSent, setSupportSent] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    Promise.all([
      apiFetch('/api/web/my-stakes').then(setStakes),
      apiFetch('/api/web/referral').then(setRefData),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  const won    = stakes.filter(s => s.status==='won');
  const lost   = stakes.filter(s => s.status==='lost');
  const active = stakes.filter(s => s.status==='active');
  const accuracy = stakes.length > 0 ? Math.round((won.length/stakes.filter(s=>s.status!=='active').length||1)*100) : 0;
  const totalWon = won.reduce((s,st) => s+st.payout, 0);

  const filteredStakes = filter==='all'?stakes:filter==='won'?won:filter==='lost'?lost:active;

  function copyLink(link) {
    navigator.clipboard.writeText(link).then(() => showToast('Link copied!', 'success')).catch(() => showToast('Copy failed', 'error'));
  }

  async function submitDispute() {
    if (!disputeReason || disputeReason.length < 10) return showToast('Please provide more detail', 'error');
    setDisputeLoading(true);
    try {
      await apiFetch('/api/web/disputes', { method:'POST', body:{ marketId: disputeModal.market_id, reason: disputeReason }});
      showToast('Dispute submitted! We will review within 24 hours.', 'success');
      setDisputeModal(null);
      setDisputeReason('');
    } catch(err) { showToast(err.message, 'error'); }
    setDisputeLoading(false);
  }

  async function sendSupport() {
    if (!supportMsg.trim() || supportMsg.length < 5) return showToast('Message too short', 'error');
    setSupportLoading(true);
    try {
      await apiFetch('/api/web/support', { method:'POST', body:{ message: supportMsg }});
      setSupportSent(true);
      setSupportMsg('');
      showToast('Message sent to support! ✅', 'success');
    } catch(err) { showToast(err.message, 'error'); }
    setSupportLoading(false);
  }

  async function linkTelegram(tgData) {
    setLinking(true);
    try {
      const data = await apiFetch('/api/web/auth/link-telegram', { method:'POST', body:tgData });
      showToast('Telegram account linked! Your history is now synced.', 'success');
      await loadMe();
    } catch(err) { showToast(err.message, 'error'); }
    setLinking(false);
  }

  useEffect(() => {
    if (tab === 'account') {
      window.onTgLinkAuth = linkTelegram;
      const el = document.getElementById('tg-link-widget');
      if (el && !el.hasChildNodes()) {
        const script = document.createElement('script');
        script.src = 'https://telegram.org/js/telegram-widget.js?22';
        script.setAttribute('data-telegram-login', 'NaijaPredict_Bot');
        script.setAttribute('data-size', 'medium');
        script.setAttribute('data-onauth', 'onTgLinkAuth(user)');
        script.setAttribute('data-request-access', 'write');
        script.setAttribute('data-userpic', 'false');
        script.async = true;
        el.appendChild(script);
      }
    }
    return () => { delete window.onTgLinkAuth; };
  }, [tab]);

  return (
    <div>
      {disputeModal && (
        <DisputeModal
          stake={disputeModal}
          reason={disputeReason}
          setReason={setDisputeReason}
          onSubmit={submitDispute}
          onClose={() => { setDisputeModal(null); setDisputeReason(''); }}
          loading={disputeLoading}
        />
      )}
      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:1.5, marginBottom:4 }}>PROFILE</div>
        <div style={{ fontSize:13, color:'var(--text2)' }}>Hi {user?.firstName||'there'} 👋</div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:24 }}>
        {[
          { label:'Balance',     value:`${user?.balance||0} ⭐`,        color:'#f5c842' },
          { label:'Total Bets',  value:stakes.length,                    color:'var(--text)' },
          { label:'Win Rate',    value:`${accuracy}%`,                   color:accuracy>=60?'#00c566':accuracy>=40?'#f5c842':'#ff6b35' },
          { label:'Total Won',   value:`${totalWon} ⭐`,                 color:'#00c566' },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign:'center', padding:'14px 8px' }}>
            <div style={{ fontSize:18, fontWeight:700, color:s.color, fontFamily:"'Bebas Neue',sans-serif" }}>{s.value}</div>
            <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'var(--bg1)', borderRadius:12, padding:4, overflowX:'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex:1, minWidth:60, padding:'8px 4px', borderRadius:8, border:'none', cursor:'pointer', fontSize:11, fontWeight:600,
            background: tab===t.id ? 'var(--bg2)' : 'transparent',
            color: tab===t.id ? 'var(--text)' : 'var(--text2)',
            display:'flex', flexDirection:'column', alignItems:'center', gap:3,
            boxShadow: tab===t.id ? '0 1px 4px rgba(0,0,0,.3)' : 'none',
          }}>
            <span style={{ fontSize:16 }}>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* MY BETS TAB */}
      {tab === 'stakes' && (
        <div>
          {/* Filter */}
          <div style={{ display:'flex', gap:6, marginBottom:16 }}>
            {[['all','All'],['active','Active'],['won','Won'],['lost','Lost']].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                background: filter===k?'var(--orange)':'var(--bg1)',
                color: filter===k?'#000':'var(--text2)'
              }}>{l} {k==='all'?stakes.length:k==='won'?won.length:k==='lost'?lost.length:active.length}</button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:'var(--muted)' }}>Loading...</div>
          ) : filteredStakes.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--muted)' }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🎯</div>
              <div style={{ fontWeight:600, marginBottom:4 }}>No {filter==='all'?'':filter} bets yet</div>
              <div style={{ fontSize:12 }}>Head to Markets to place your first prediction</div>
              <button className="btn-primary" onClick={() => navigate('/')} style={{ marginTop:16, padding:'10px 24px' }}>Browse Markets</button>
            </div>
          ) : filteredStakes.map(s => (
            <div key={s.id} className="card" style={{ marginBottom:10, padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <div style={{ fontSize:13, fontWeight:600, flex:1, marginRight:12, lineHeight:1.4 }}>{s.title}</div>
                <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, flexShrink:0,
                  background: s.status==='won'?'rgba(0,197,102,.1)':s.status==='lost'?'rgba(255,68,68,.1)':s.status==='active'?'rgba(14,143,255,.1)':'var(--bg3)',
                  color: s.status==='won'?'#00c566':s.status==='lost'?'#ff4444':s.status==='active'?'#0e8fff':'var(--muted)' }}>
                  {s.status==='won'?'✅ Won':s.status==='lost'?'❌ Lost':s.status==='active'?'⏳ Active':'↩️ Refunded'}
                </span>
              </div>
              <div style={{ display:'flex', gap:16, fontSize:11, color:'var(--muted)', flexWrap:'wrap' }}>
                <span style={{ color:s.side==='YES'?'#00c566':'#ff6b35', fontWeight:600 }}>{s.side}</span>
                <span>Staked: <strong style={{ color:'var(--text)' }}>{s.amount} ⭐</strong></span>
                {s.payout > 0 && <span>Payout: <strong style={{ color:'#f5c842' }}>{s.payout} ⭐</strong></span>}
                {s.payout > 0 && s.status==='won' && <span style={{ color:'#00c566' }}>+{s.payout-s.amount} ⭐ profit</span>}
                <span>{new Date(s.created_at).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}</span>
                {s.status==='lost' && s.market_status==='resolved' && (
                  <button onClick={() => setDisputeModal(s)} style={{ marginTop:6, padding:'4px 10px', background:'rgba(255,107,53,.06)', border:'1px solid rgba(255,107,53,.2)', borderRadius:8, color:'var(--orange)', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                    ⚠️ Dispute
                  </button>
                )}
                <span style={{ color: {Sports:'#00c566',Politics:'#0e8fff',Economy:'#f5c842',Entertainment:'#ff6b35'}[s.category]||'var(--muted)' }}>{s.category}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TOP TAB */}
      {tab === 'top' && (
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:1, marginBottom:16 }}>YOUR PERFORMANCE</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
            {[
              { label:'Total Bets',      value:stakes.length,                color:'var(--text)'  },
              { label:'Wins',            value:won.length,                   color:'#00c566'      },
              { label:'Losses',          value:lost.length,                  color:'#ff4444'      },
              { label:'Active',          value:active.length,                color:'#0e8fff'      },
              { label:'Win Rate',        value:`${accuracy}%`,               color:accuracy>=60?'#00c566':accuracy>=40?'#f5c842':'#ff6b35' },
              { label:'Total Staked',    value:`${stakes.reduce((s,st)=>s+st.amount,0)} ⭐`, color:'var(--text)' },
              { label:'Total Won',       value:`${totalWon} ⭐`,             color:'#00c566'      },
              { label:'Net Profit',      value:`${totalWon - stakes.reduce((s,st)=>s+st.amount,0)} ⭐`, color: totalWon >= stakes.reduce((s,st)=>s+st.amount,0) ? '#00c566' : '#ff4444' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding:'14px' }}>
                <div style={{ fontSize:20, fontWeight:700, color:s.color, fontFamily:"'Bebas Neue',sans-serif" }}>{s.value}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Category breakdown */}
          <div className="card">
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>By Category</div>
            {['Sports','Politics','Economy','Entertainment','World'].map(cat => {
              const catStakes = stakes.filter(s=>s.category===cat);
              const catWon = catStakes.filter(s=>s.status==='won').length;
              const catAcc = catStakes.length > 0 ? Math.round(catWon/catStakes.filter(s=>s.status!=='active').length*100)||0 : 0;
              if (catStakes.length === 0) return null;
              return (
                <div key={cat} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontSize:13 }}>{cat}</div>
                  <div style={{ display:'flex', gap:16, fontSize:12, color:'var(--text2)' }}>
                    <span>{catStakes.length} bets</span>
                    <span style={{ color:catAcc>=60?'#00c566':catAcc>=40?'#f5c842':'#ff6b35', fontWeight:600 }}>{catAcc}% win rate</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INVITE TAB */}
      {tab === 'invite' && (
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:1, marginBottom:4 }}>INVITE & EARN</div>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>Invite friends and earn Stars when they stake for the first time!</div>

          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
            <div className="card" style={{ textAlign:'center' }}>
              <div style={{ fontSize:28, fontWeight:700, color:'#ff6b35', fontFamily:"'Bebas Neue',sans-serif" }}>{refData?.totalReferrals||0}</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>Friends Invited</div>
            </div>
            <div className="card" style={{ textAlign:'center' }}>
              <div style={{ fontSize:28, fontWeight:700, color:'#f5c842', fontFamily:"'Bebas Neue',sans-serif" }}>{refData?.totalEarned||0} ⭐</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>Stars Earned</div>
            </div>
          </div>

          {/* How it works */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>How it works</div>
            {[
              ['1','Share your link','Send your unique link to friends'],
              ['2','Friend signs up','They create a NaijaPredict account'],
              ['3','They stake','When they place their first bet, you earn!'],
            ].map(([n,t,s]) => (
              <div key={n} style={{ display:'flex', gap:12, marginBottom:12, alignItems:'flex-start' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#ff6b35,#f5c842)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#000', flexShrink:0 }}>{n}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{t}</div>
                  <div style={{ fontSize:11, color:'var(--muted)' }}>{s}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Share links */}
          <div className="card" style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, color:'var(--text2)', fontWeight:600, marginBottom:8 }}>WEB INVITE LINK</div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ flex:1, padding:'10px 12px', background:'var(--bg2)', borderRadius:8, fontSize:12, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {refData?.webRefLink||'Loading...'}
              </div>
              <button onClick={() => copyLink(refData?.webRefLink||'')} className="btn-primary" style={{ padding:'10px 14px', fontSize:12, flexShrink:0 }}>Copy</button>
            </div>
          </div>

          <div className="card" style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, color:'var(--text2)', fontWeight:600, marginBottom:8 }}>TELEGRAM BOT LINK</div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ flex:1, padding:'10px 12px', background:'var(--bg2)', borderRadius:8, fontSize:12, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {refData?.refLink||'Loading...'}
              </div>
              <button onClick={() => copyLink(refData?.refLink||'')} className="btn-primary" style={{ padding:'10px 14px', fontSize:12, flexShrink:0 }}>Copy</button>
            </div>
          </div>

          {/* Referral list */}
          {refData?.referrals?.length > 0 && (
            <div className="card">
              <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>Your Referrals</div>
              {refData.referrals.map((r,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{r.first_name||'User'}</div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>Joined {new Date(r.joined_at).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, color: r.first_stake_done?'#00c566':'#f5c842', fontWeight:600 }}>
                      {r.first_stake_done ? '✅ Staked' : '⏳ Not staked yet'}
                    </div>
                    {r.bonus_paid > 0 && <div style={{ fontSize:11, color:'#f5c842' }}>+{r.bonus_paid} ⭐ earned</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HELP TAB */}
      {tab === 'help' && (
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:1, marginBottom:16 }}>HELP & SUPPORT</div>
          {[
            { q:'How do I earn Stars?', a:'Stake on markets and win! You also get 100 Stars free when you sign up, and 5 Stars for each friend you refer who places a bet.' },
            { q:'How do I withdraw?', a:'Go to Store → Withdraw. You need at least 200 Stars earned from winning predictions. Signup bonus Stars must be used for staking first.' },
            { q:'What is a prediction market?', a:'You predict YES or NO on an event. If correct, you win a share of the opposing pool. The more people bet against you, the bigger your win.' },
            { q:'Why did I win less than I staked?', a:'This was a bug we fixed. All winners now receive at least 10% profit. One-sided markets (where everyone bets the same side) are fully refunded.' },
            { q:'How long does withdrawal take?', a:'Withdrawals are processed within 24 hours directly to your Nigerian bank account via Flutterwave.' },
            { q:'What is the League?', a:'A weekly competition where you pay an entry fee, get bonus Stars, and compete for the prize pool. Top predictors win big!' },
            { q:'How is the league scored?', a:'Points are based on correct predictions. Brave predictions (underdog wins) earn more points than safe ones. Top 3 win prizes.' },
            { q:'Can I use NaijaPredict on Telegram?', a:'Yes! Our full-featured Telegram bot @NaijaPredict_Bot has all the same features plus real-time notifications.' },
          ].map((item,i) => (
            <HelpItem key={i} q={item.q} a={item.a}/>
          ))}
          <div className="card" style={{ marginTop:16, background:'rgba(41,182,246,.04)', border:'1px solid rgba(41,182,246,.2)' }}>
            <div style={{ fontSize:14, fontWeight:700, marginBottom:4 }}>📩 Contact Support</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14, lineHeight:1.6 }}>Send us a message and we'll respond as soon as possible.</div>
            {supportSent ? (
              <div style={{ textAlign:'center', padding:'16px', background:'rgba(0,197,102,.06)', border:'1px solid rgba(0,197,102,.2)', borderRadius:10 }}>
                <div style={{ fontSize:24, marginBottom:6 }}>✅</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#00c566' }}>Message sent!</div>
                <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>We'll get back to you soon.</div>
                <button onClick={()=>setSupportSent(false)} style={{ marginTop:10, background:'none', border:'1px solid var(--border)', borderRadius:8, color:'var(--text2)', padding:'6px 14px', cursor:'pointer', fontSize:12 }}>Send another</button>
              </div>
            ) : (
              <div>
                <textarea
                  placeholder="Describe your issue or question..."
                  value={supportMsg}
                  onChange={e => setSupportMsg(e.target.value)}
                  rows={4}
                  style={{ width:'100%', padding:'12px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.6, marginBottom:8 }}/>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <span style={{ fontSize:11, color:'var(--muted)' }}>{supportMsg.length}/1000</span>
                  {supportMsg.length > 0 && <span style={{ fontSize:11, color:supportMsg.length>=5?'#00c566':'#ff4444' }}>{supportMsg.length>=5?'✅ Ready':'Too short'}</span>}
                </div>
                <button className="btn-primary" onClick={sendSupport}
                  disabled={supportLoading||supportMsg.length<5}
                  style={{ width:'100%', padding:13, fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {supportLoading?'Sending...':<><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>Send to Support</>}
                </button>
              </div>
            )}
            <a href="https://telegram.me/NaijaPredictGroup" target="_blank" rel="noreferrer" style={{ display:'block', textAlign:'center', marginTop:12, fontSize:12, color:'#29b6f6' }}>💬 Join Community Group</a>
          <div style={{ display:'flex', gap:16, justifyContent:'center', marginTop:14, fontSize:11, color:'var(--muted)' }}>
            <a href="/terms" style={{ color:'var(--orange)' }}>Terms & Conditions</a>
            <span>·</span>
            <a href="/privacy" style={{ color:'var(--orange)' }}>Privacy Policy</a>
          </div>
          </div>
        </div>
      )}

      {/* ACCOUNT TAB */}
      {tab === 'account' && (
        <div>
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, marginBottom:16 }}>Account Info</div>
            <div style={{ display:'grid', gap:12 }}>
              {[
                ['Name', user?.firstName||'—'],
                ['Username', '@'+(user?.username||'—')],
                ['Email', user?.email||'—'],
                ['Phone', user?.phone||'—'],
                ['Auth Provider', user?.authProvider||'—'],
                ['Balance', `${user?.balance||0} ⭐`],
                ['Withdrawable', `${user?.withdrawableBalance||0} ⭐`],
              ].map(([label,val]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:12, color:'var(--muted)' }}>{label}</span>
                  <span style={{ fontSize:13, fontWeight:600 }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* NIN Status */}
          <div className="card" style={{ marginBottom:16, background: user?.ninVerified ? 'rgba(0,197,102,.04)' : 'rgba(255,107,53,.04)', border:`1px solid ${user?.ninVerified ? 'rgba(0,197,102,.2)' : 'rgba(255,107,53,.2)'}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: user?.ninVerified ? 0 : 12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:20 }}>🪪</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700 }}>NIN Verification</div>
                  <div style={{ fontSize:11, color: user?.ninVerified ? '#00c566' : '#ff6b35' }}>
                    {user?.ninVerified ? '✅ Verified' : '⚠️ Not verified'}
                  </div>
                </div>
              </div>
            </div>
            {!user?.ninVerified && <NINVerification onVerified={() => loadMe()}/>}
          </div>

          {/* Link Telegram Account */}
          {user?.authProvider === 'email' && (
            <div className="card" style={{ marginBottom:16, background:'rgba(41,182,246,.04)', border:'1px solid rgba(41,182,246,.2)' }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:4, color:'#29b6f6' }}>🔗 Link Your Telegram Account</div>
              <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12, lineHeight:1.6 }}>
                Already use NaijaPredict on Telegram? Link your account to sync your full betting history, balance and rewards to this web account.
              </div>
              {linking ? (
                <div style={{ fontSize:13, color:'var(--muted)' }}>Linking account...</div>
              ) : (
                <div id="tg-link-widget"/>
              )}
            </div>
          )}

          {user?.authProvider === 'linked' && (
            <div style={{ fontSize:12, color:'#00c566', marginBottom:16, padding:'10px 14px', background:'rgba(0,197,102,.06)', border:'1px solid rgba(0,197,102,.2)', borderRadius:10 }}>
              ✅ Telegram account linked — your history is synced
            </div>
          )}

          <div style={{ display:'grid', gap:10 }}>
            <button onClick={() => navigate('/store')} className="btn-ghost" style={{ width:'100%', padding:13, textAlign:'left', display:'flex', justifyContent:'space-between' }}>
              <span>💳 Top Up Stars</span><span>→</span>
            </button>
            <button onClick={() => navigate('/store?tab=withdraw')} className="btn-ghost" style={{ width:'100%', padding:13, textAlign:'left', display:'flex', justifyContent:'space-between' }}>
              <span>🏦 Withdraw to Bank</span><span>→</span>
            </button>
            <button onClick={() => setTab('invite')} className="btn-ghost" style={{ width:'100%', padding:13, textAlign:'left', display:'flex', justifyContent:'space-between' }}>
              <span>👥 Invite Friends</span><span>→</span>
            </button>
            <button onClick={logout} style={{ width:'100%', padding:13, background:'rgba(255,68,68,.08)', border:'1px solid rgba(255,68,68,.2)', borderRadius:10, color:'#ff4444', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DisputeModal({ stake, reason, setReason, onSubmit, onClose, loading }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:100, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg1)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:520, padding:'24px 20px 34px', border:'1px solid var(--border)', borderBottom:'none' }}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>⚠️ Dispute Resolution</div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>{stake.title}</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:16 }}>
          Your stake: <strong>{stake.amount} ⭐</strong> on <span style={{color:stake.side==='YES'?'#00c566':'#ff6b35',fontWeight:600}}>{stake.side}</span> · Result: <strong>{stake.status==='won'?'✅ Won':'❌ Lost'}</strong>
        </div>
        <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.5 }}>REASON FOR DISPUTE</label>
        <textarea
          placeholder="Explain why you believe this market was resolved incorrectly..."
          value={reason} onChange={e => setReason(e.target.value)} rows={4}
          style={{ width:'100%', marginTop:6, marginBottom:8, padding:'12px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
        />
        <div style={{ fontSize:11, color:'var(--muted)', marginBottom:16 }}>{reason.length}/500 · Min 10 characters</div>
        <button className="btn-primary" onClick={onSubmit} disabled={loading||reason.length<10} style={{ width:'100%', padding:13, fontSize:14, marginBottom:10 }}>
          {loading ? 'Submitting...' : 'Submit Dispute'}
        </button>
        <button onClick={onClose} style={{ width:'100%', padding:10, background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:13 }}>Cancel</button>
      </div>
    </div>
  );
}

function HelpItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom:8, padding:0, overflow:'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{ width:'100%', padding:'14px 16px', background:'none', border:'none', color:'var(--text)', fontSize:13, fontWeight:600, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        {q}
        <span style={{ fontSize:16, color:'var(--muted)', transform:open?'rotate(180deg)':'none', transition:'transform .2s' }}>▾</span>
      </button>
      {open && <div style={{ padding:'0 16px 14px', fontSize:13, color:'var(--text2)', lineHeight:1.7, borderTop:'1px solid var(--border)' }}>{a}</div>}
    </div>
  );
}
